class SwiperConfigurator {
  static swiperInstances = new Map();
  static swiperFilesPromise = null;

  constructor(sliderElement) {
    this.element = sliderElement;
    this.options = this.parseConfig();
    this.setupEventListeners();

    if (this.options.observer) {
      this.initIntersectionObserver();
    } else {
      this.initializeSwiper();
    }
  }

  parseConfig() {
    const { dataset: { config, mobile, observer } } = this.element;

    try {
      return {
        config: JSON.parse(config),
        mobile: mobile === "true",
        observer: observer === "true"
      };
    } catch (error) {
      console.error("Error parsing slider configuration:", error);
      return { config: {}, mobile: false, observer: false };
    }
  }

  initializeSwiper() {
    if (this.shouldDisableSwiper()) {
      return;
    }
    if (!Swiper || !this.element || this.element.swiper) {
      return;
    }
    if (this.options.config.thumbs?.swiper) {
      const thumbsSelector = this.options.config.thumbs.swiper;
      const thumbsElement = document.querySelector(thumbsSelector);
      const thumbsInstance = SwiperConfigurator.swiperInstances.get(thumbsElement);

      if (!thumbsInstance) {
        setTimeout(() => this.initializeSwiper(), 0);
        return;
      }

      this.options.config.thumbs.swiper = thumbsInstance;
    }

    const instance = new Swiper(this.element, this.options.config);
    SwiperConfigurator.swiperInstances.set(this.element, instance)
  }

  shouldDisableSwiper() {
    return this.options.mobile && window.innerWidth > 768;
  }

  setupEventListeners() {
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  handleResize() {
    if (this.shouldDisableSwiper()) {
      this.destroySwiper();
    } else {
      this.initializeSwiper();
    }
  }

  destroySwiper() {
    if (this.element && this.element.swiper) {
      this.element.swiper.destroy(true, true);
    }
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log('Element is in view, initializing swiper');
          this.observer.disconnect();
          this.initializeSwiper();
        }
      });
    }, { threshold: 0.1 });

    this.observer.observe(this.element);
  }

  static appendTagToBody(tagType, filePath) {
    return new Promise((resolve, reject) => {
      const tag = document.createElement(tagType === 'script' ? 'script' : 'link');

      if (tagType === 'script') {
        tag.src = filePath;
        tag.type = 'text/javascript';
      } else if (tagType === 'style') {
        tag.href = filePath;
        tag.rel = 'stylesheet';
      } else {
        return reject(new Error('Unsupported tag type. Please use "script" or "style".'));
      }

      tag.onload = resolve;
      tag.onerror = () => reject(new Error(`Failed to load ${tagType} file: ${filePath}`));

      document.body.appendChild(tag);
    });
  }

  static appendSwiperFiles() {
    if (SwiperConfigurator.swiperFilesPromise) {
      return SwiperConfigurator.swiperFilesPromise;
    }
    const { css, js } = window.swiperFile || {};
    const files = [
      { type: 'style', path: css },
      { type: 'script', path: js },
    ].filter(file => file.path);

    if (files.length === 0) {
      SwiperConfigurator.swiperFilesPromise = Promise.resolve();
      return SwiperConfigurator.swiperFilesPromise;
    }
    const loadFilePromises = files.map(file => SwiperConfigurator.appendTagToBody(file.type, file.path));
    SwiperConfigurator.swiperFilesPromise = Promise.all(loadFilePromises);
    return SwiperConfigurator.swiperFilesPromise;
  }
}

const initSwipers = (root = document) => {
  const sliderElements = root.querySelectorAll("[data-slider-selector]");

  if (sliderElements.length > 0) {
    SwiperConfigurator.appendSwiperFiles()
      .then(() => {
        const sliders = Array.from(sliderElements)
          .filter(slider => !slider.hasAttribute('data-slider-ingredients'));
        const hasThumbs = (slider) =>
          typeof slider.dataset.config === 'string' &&
          slider.dataset.config.includes('"thumbs"');

        sliders
          .sort((a, b) => Number(hasThumbs(a)) - Number(hasThumbs(b)))
          .forEach((slider) => {
            if (slider.swiper || slider.dataset.swiperConfigured === 'true') return;
            slider.dataset.swiperConfigured = 'true';
            new SwiperConfigurator(slider);
          });
      }).catch((error) => {
        console.error('Failed to load Swiper files:', error);
      });
  }
};

document.addEventListener('DOMContentLoaded', () => {
  initSwipers();
});

document.addEventListener('shopify:section:load', (event) => {
  initSwipers(event.target);
});